// Commercial default column set. Every column ships visible by default — including the two Quote
// placeholders, which render "—" until quote data is fetched. Column visibility is stored per-user in
// localStorage, under a key that is independent of the Projects list ('columns-projects-overview-v3')
// and from the Client-Opportunities tab. The Opportunity Status column shows only under the All tab.

import type {OpportunityFilterTab, OpportunitySortKey} from './opportunityFilters';

export type OpportunityColumnName =
	| 'name'
	| 'client'
	| 'primaryContact'
	| 'salesperson'
	| 'stage'
	| 'labels'
	| 'winProbability'
	| 'targetValue'
	| 'startDate'
	| 'endDate'
	| 'quoteStatus'
	| 'quoteExpiry'
	| 'status';

export interface OpportunityColumn {
	name: OpportunityColumnName;
	messageId: string;
	numeric: boolean;
	financial: boolean;
	allTabOnly: boolean;
	defaultVisible: boolean;
}

// Left→right order per the design: Opportunity · Client · Primary Contact · Target Value · Win Probability ·
// Opportunity Stage · Labels · Start Date · End Date · Quote Status · Quote Expiry · Opportunity Status · Salesperson.
export const OPPORTUNITY_COLUMNS: OpportunityColumn[] = [
	{
		name: 'name',
		messageId: 'opportunities.column.name',
		numeric: false,
		financial: false,
		allTabOnly: false,
		defaultVisible: true,
	},
	{name: 'client', messageId: 'common.client', numeric: false, financial: false, allTabOnly: false, defaultVisible: true},
	{
		name: 'primaryContact',
		messageId: 'opportunities.column.primary_contact',
		numeric: false,
		financial: false,
		allTabOnly: false,
		defaultVisible: true,
	},
	{
		name: 'targetValue',
		messageId: 'opportunities.column.target_value',
		numeric: true,
		financial: true,
		allTabOnly: false,
		defaultVisible: true,
	},
	{
		name: 'winProbability',
		messageId: 'common.win_probability',
		numeric: true,
		financial: true,
		allTabOnly: false,
		defaultVisible: true,
	},
	{
		name: 'stage',
		messageId: 'opportunities.column.stage',
		numeric: false,
		financial: false,
		allTabOnly: false,
		defaultVisible: true,
	},
	{
		name: 'labels',
		messageId: 'common.labels',
		numeric: false,
		financial: false,
		allTabOnly: false,
		defaultVisible: false,
	},
	{
		name: 'startDate',
		messageId: 'opportunities.column.start_date',
		numeric: false,
		financial: false,
		allTabOnly: false,
		defaultVisible: true,
	},
	{
		name: 'endDate',
		messageId: 'opportunities.column.end_date',
		numeric: false,
		financial: false,
		allTabOnly: false,
		defaultVisible: true,
	},
	{
		name: 'quoteStatus',
		messageId: 'opportunities.column.quote_status',
		numeric: false,
		financial: false,
		allTabOnly: false,
		defaultVisible: true,
	},
	{
		name: 'quoteExpiry',
		messageId: 'opportunities.column.quote_expiry',
		numeric: false,
		financial: false,
		allTabOnly: false,
		defaultVisible: true,
	},
	{
		name: 'status',
		messageId: 'opportunities.column.status',
		numeric: false,
		financial: false,
		allTabOnly: true,
		defaultVisible: true,
	},
	{
		name: 'salesperson',
		messageId: 'opportunities.column.salesperson',
		numeric: false,
		financial: false,
		allTabOnly: false,
		defaultVisible: true,
	},
];

// Preference scope — mirrors opportunityFilters' DEFAULT_PREFERENCE_SCOPE. The default reproduces the
// original 'columns-opportunities-overview-v1' key exactly, so the global page's stored preferences are
// unchanged; a host passing a distinct scope (e.g. the client tab) gets its own independent key.
export const DEFAULT_PREFERENCE_SCOPE = 'opportunities-overview';
const columnKey = (scope: string): string => `columns-${scope}-v1`;

function defaultVisibility(): Record<OpportunityColumnName, boolean> {
	return OPPORTUNITY_COLUMNS.reduce((acc, c) => {
		acc[c.name] = c.defaultVisible;
		return acc;
	}, {} as Record<OpportunityColumnName, boolean>);
}

export function readColumnVisibility(scope: string = DEFAULT_PREFERENCE_SCOPE): Record<OpportunityColumnName, boolean> {
	const defaults = defaultVisibility();
	try {
		const raw = localStorage.getItem(columnKey(scope));
		if (!raw) return defaults;
		const stored = JSON.parse(raw) as Partial<Record<OpportunityColumnName, boolean>>;
		// Merge onto defaults so a newly-added column shows up even for users with an older stored map.
		return {...defaults, ...stored};
	} catch {
		return defaults;
	}
}

export function writeColumnVisibility(
	v: Record<OpportunityColumnName, boolean>,
	scope: string = DEFAULT_PREFERENCE_SCOPE
): void {
	try {
		localStorage.setItem(columnKey(scope), JSON.stringify(v));
	} catch {
		/* localStorage unavailable */
	}
}

export function getVisibleColumns(args: {
	tab: OpportunityFilterTab;
	visibility: Record<OpportunityColumnName, boolean>;
	canViewFinancials: boolean;
	// When the list is scoped to a single client (client-detail Opportunities tab), every row belongs
	// to that client, so the Client column is redundant and hidden. Defaults to false (global page).
	hideClientColumn?: boolean;
}): OpportunityColumn[] {
	const {tab, visibility, canViewFinancials, hideClientColumn = false} = args;
	return OPPORTUNITY_COLUMNS.filter(col => {
		if (col.name === 'client' && hideClientColumn) return false;
		if (col.allTabOnly && tab !== 'all') return false;
		if (col.financial && !canViewFinancials) return false;
		return visibility[col.name] !== false;
	});
}

// Maps a column header to the sort key it drives (null = not sortable). Extracted so the page's
// header onClick has no ternary chain. Every data column is sortable — including Opportunity Name,
// Salesperson and both Quote columns — and orders by its own comparator.
export function sortKeyForColumn(name: OpportunityColumnName): OpportunitySortKey | null {
	switch (name) {
		case 'name':
			return 'name';
		case 'client':
			return 'client';
		case 'primaryContact':
			return 'contact';
		case 'salesperson':
			return 'salesperson';
		case 'targetValue':
			return 'targetValue';
		case 'winProbability':
			return 'winChance';
		case 'stage':
			return 'stage';
		case 'labels':
			return 'labels';
		case 'startDate':
			return 'startDate';
		case 'endDate':
			return 'endDate';
		case 'status':
			return 'status';
		case 'quoteStatus':
			return 'quoteStatus';
		case 'quoteExpiry':
			return 'quoteExpiry';
		default:
			return null;
	}
}

/**
 * The design-system column id for a native column: its sort key (sortKeyForColumn), which is where the
 * name-to-id differences live, or the name itself for a column with no sort key. The saved column layout
 * is keyed by this id; opportunityColumnsView.test.tsx checks the view's `id` props against it.
 */
export function columnIdFor(name: OpportunityColumnName): string {
	return sortKeyForColumn(name) ?? name;
}
